"use client";
import { IUserData } from "@/types";
import {
    HeadingStyleBanner,
    HeadingStyleBase,
    HeadingStyleSideways,
    HeadingStyleHeadshot,
    HeadingStyleBanner2,
} from "./HeadingStyle";
import { useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDynamicSidebar } from "@/lib/hooks/use-dynamic-sidebar";
import { useSettingsSidebar } from "@/hooks/useSettingsSidebar";
import { Label } from "../ui/label";

interface Props {
    account: IUserData;
    theme: IUserData["chosen_theme"];
    edit?: boolean;
}

export function HeadingDefault({ account, theme, edit }: Props) {
    if (!edit) {
        return <HeadingDefaultInternal account={account} theme={theme} />;
    }
    const popoverRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const { isOpen: sidebarIsOpen, toggleSidebar: toggleSettingsSidebar } =
        useDynamicSidebar(["render-settings-sidebar-state"]);
    let {
        setValue: setSidebarView,
        value: sideBarView,
        selectedBlock: { setValue: setSelectedBlock, value: selectedBlock },
    } = useSettingsSidebar();
    const isExpanded = sideBarView === "heading";
    useEffect(() => {
        if (popoverRef.current && isMobile) {
            popoverRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        } else if (popoverRef.current && isExpanded) {
            const rect = popoverRef.current.getBoundingClientRect();
            // Check if any part of the element is within the viewport
            const isVisible =
                rect.top < window.innerHeight &&
                rect.bottom >= 0 &&
                rect.left < window.innerWidth &&
                rect.right >= 0;
            if (!isVisible) {
                popoverRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }
    }, [isMobile, isExpanded]);
    const handleExpand = () => {
        if (!sidebarIsOpen) {
            toggleSettingsSidebar();
        }
        setSidebarView("heading");
        setSelectedBlock(null);
    };
    const handleClose = () => {
        setSidebarView("root");
    };
    useEffect(() => {
        if (!sidebarIsOpen) {
            handleClose();
        }
    }, [sidebarIsOpen]);
    return (
        <div
            ref={popoverRef}
            onClick={() => {
                if (isExpanded) {
                    handleClose();
                } else {
                    handleExpand();
                }
            }}
            className={`group relative z-40 w-full cursor-pointer rounded-md border-2 p-1 transition-all duration-300 hover:border-solid hover:border-primary data-[state=open]:border-solid data-[state=open]:border-primary
                ${isExpanded ? "border border-primary" : "border-transparent"}                `}
        >
            <div className="relative flex w-full cursor-pointer flex-col gap-1">
                <div
                    className={`absolute -right-1 -top-1 z-40 flex items-center rounded-bl-md bg-primary p-1 opacity-0 transition-all duration-300 group-hover:opacity-100
                        ${isExpanded && "opacity-100"}`}
                >
                    <Label variant="toggle">Heading</Label>
                </div>
                <div className={`w-full ${edit && ""}`}>
                    <div
                        className={`w-full ${edit && "absolute left-0 top-0 z-40 flex h-full justify-center rounded-md transition-all duration-300 group-hover:bg-primary/30"}`}
                    />
                    <HeadingDefaultInternal account={account} theme={theme} />
                </div>
            </div>
        </div>
    );
}
export function HeadingDefaultInternal({ account, theme }: Props) {
    const renderContent = () => {
        switch (theme.heading.type) {
            default:
                return <HeadingStyleBase account={account} theme={theme} />;
            case "sideways":
                return <HeadingStyleSideways account={account} theme={theme} />;
            case "banner":
                return <HeadingStyleBanner account={account} theme={theme} />;
            case "banner2":
                return <HeadingStyleBanner2 account={account} theme={theme} />;
            case "headshot":
                return <HeadingStyleHeadshot account={account} theme={theme} />;
        }
    };
    return renderContent();
}
